import { useSyncExternalStore } from "react";

// A no-op subscription: this store never changes after hydration, so it never
// needs to notify. The false→true transition is driven entirely by React's
// hydration handling of `getServerSnapshot` vs `getSnapshot` (below), not by an
// external event — so `subscribe` returns an unsubscribe that is never called.
const subscribe = () => () => {};

/**
 * `true` once the client has hydrated; `false` during SSR and on the FIRST
 * (hydration) client render.
 *
 * Use it to gate any render that depends on client-only state the server can't
 * see — most notably the localStorage-seeded SWR cache (`_chrome/swr-provider.tsx`
 * synchronously hydrates the SWR map from `localStorage` at init, so a persisted
 * key returns data on the client's first render while the server, with an empty
 * cache, rendered a loading skeleton). Rendering an SSR-identical placeholder on
 * render #0 keeps the server HTML and the first client render byte-for-byte equal
 * (no hydration mismatch); the real, already-cached content reveals on the very
 * next render — with NO refetch.
 *
 * `useSyncExternalStore` is the right primitive here (not `useState`+`useEffect`):
 * its `getServerSnapshot` is used for the SSR + hydration render (`false`), and its
 * client `getSnapshot` returns `true` synchronously on every later mount — so a
 * client-side navigation that mounts a fresh consumer reveals the cached content
 * immediately, WITHOUT re-flashing the placeholder. (A `useState(false)` gate would
 * re-flash on every mount, defeating the warm-cache instant paint.)
 */
export function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  );
}
